import React from "react";
import dayjs from "dayjs";
import api from "../../../api/api";

const YesNoToggle = ({ name, value, onChange, label }) => {
  return (
    <div className="mt-4">
      <label className="d-block mb-1">
        {label} <span className="text-danger">*</span>
      </label>
      <div className="btn-group w-100" role="group">
        <input
          type="radio"
          className="btn-check"
          name={name}
          id={`${name}_yes`}
          value="Yes"
          checked={value === "Yes"}
          onChange={onChange}
          required
        />
        <label className="btn btn-outline-primary px-5" htmlFor={`${name}_yes`}>
          Yes
        </label>

        <input
          type="radio"
          className="btn-check"
          name={name}
          id={`${name}_no`}
          value="No"
          checked={value === "No"}
          onChange={onChange}
        />
        <label
          className="btn btn-outline-secondary px-5"
          htmlFor={`${name}_no`}
        >
          No
        </label>
      </div>
    </div>
  );
};

const parseFastCardFields = (task) => {
  if (!task?.ut_custom_fields) return {};

  if (typeof task.ut_custom_fields === "string") {
    try {
      return JSON.parse(task.ut_custom_fields);
    } catch {
      return {};
    }
  }

  return task.ut_custom_fields;
};

const Step4Qualifications = ({
  form,
  job,
  handleChange,
  prevStep,
  nextStep,
  setForm,
}) => {
  const [trainings, setTrainings] = React.useState(
    form.dq_trainings || [{ type: "", date: "" }],
  );
  const [awards, setAwards] = React.useState(
    form.dq_awards || [{ type: "", date: "" }],
  );

  const [trainingDocs, setTrainingDocs] = React.useState([]);
  const [awardDocs, setAwardDocs] = React.useState([]);
  const [fastDocs, setFastDocs] = React.useState({ files: [] });
  const [fastCardFileError, setFastCardFileError] = React.useState("");
  const isDriver = [2, 8, 11].includes(job.p_category);

  const hydrateFastCardFields = React.useCallback(
    (task) => {
      const fields = parseFastCardFields(task);
      if (!fields) return;

      setForm((prev) => ({
        ...prev,
        dq_fast_card_no: prev.dq_fast_card_no || fields.cardNumber || "",
        dq_fast_card_expiry: prev.dq_fast_card_expiry || fields.expiryDate || "",
      }));
    },
    [setForm],
  );

  const addTraining = () =>
    setTrainings([...trainings, { type: "", date: "" }]);
  const addAward = () => setAwards([...awards, { type: "", date: "" }]);

  const handleTrainingChange = (index, field, value) => {
    const updated = [...trainings];
    updated[index][field] = value;
    setTrainings(updated);
  };

  const handleAwardChange = (index, field, value) => {
    const updated = [...awards];
    updated[index][field] = value;
    setAwards(updated);
  };

  const fetchDocs = async (docType, setter) => {
    try {
      const { data } = await api.get(`/api/candidate/user/docs/${docType}`);
      setter(data);
    } catch (err) {
      console.error(`Failed to fetch ${docType} docs:`, err);
    }
  };

  const fetchFastDocs = React.useCallback(async () => {
    try {
      const { data } = await api.get("/api/candidate/user/fast-card");
      setFastDocs(data || { files: [] });
      hydrateFastCardFields(data?.task);
    } catch (err) {
      console.error("Failed to fetch fast card:", err);
    }
  }, [hydrateFastCardFields]);

  React.useEffect(() => {
    if (form.dq_training === "Yes") fetchDocs("training", setTrainingDocs);
    if (form.dq_safe_driving_awards === "Yes") fetchDocs("award", setAwardDocs);
    if (form.dq_fast_card === "Yes") fetchFastDocs();
  }, [
    form.dq_training,
    form.dq_safe_driving_awards,
    form.dq_fast_card,
    fetchFastDocs,
  ]);

  React.useEffect(() => {
    if (form.dq_training === "Yes" && trainings.length === 0) {
      setTrainings([{ type: "", date: "" }]);
    }
    if (form.dq_training === "No" && trainings.length > 0) {
      setTrainings([]);
    }
  }, [form.dq_training, trainings.length]);

  React.useEffect(() => {
    if (form.dq_safe_driving_awards === "Yes" && awards.length === 0) {
      setAwards([{ type: "", date: "" }]);
    }
    if (form.dq_safe_driving_awards === "No" && awards.length > 0) {
      setAwards([]);
    }
  }, [form.dq_safe_driving_awards, awards.length]);

  const handleFastUpload = async (files) => {
    if (!files || !files.length) return;

    if (!form.dq_fast_card_no || !form.dq_fast_card_expiry) {
      setFastCardFileError(
        "Enter FAST Card number and expiry date before uploading.",
      );
      return;
    }

    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    fd.append("fc_number", form.dq_fast_card_no);
    fd.append("fc_expiry_date", form.dq_fast_card_expiry);

    try {
      await api.post("/api/candidate/user/upload-fast-card", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFastCardFileError("");
      await fetchFastDocs();
    } catch (err) {
      console.error(err);
      alert("FAST Card upload failed");
    }
  };

  const handleUpload = async (files, docType, setter) => {
    if (!files || !files.length) return;

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];

    for (const f of Array.from(files)) {
      if (!allowedTypes.includes(f.type)) {
        alert("Only PDF, JPG, or PNG files are allowed");
        return;
      }
    }

    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));

    try {
      await api.post(`/api/candidate/user/upload-${docType}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      fetchDocs(docType, setter);
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Upload failed");
    }
  };

  const handleDelete = async (docId, docType, setter) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/api/candidate/user/docs/${docId}`);
      fetchDocs(docType, setter);
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Delete failed");
    }
  };

  const handleFastDelete = async (fileId) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await api.delete(`/api/candidate/user/fast-card/file/${fileId}`);
      await fetchFastDocs();
    } catch (err) {
      alert("Delete failed");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const hasFastCardFile = (fastDocs.files || []).length > 0;

    if (form.dq_fast_card === "Yes" && !hasFastCardFile) {
      setFastCardFileError("FAST Card file is required.");
      return;
    }

    setFastCardFileError("");
    if (e.target.checkValidity()) {
      setForm((prev) => ({
        ...prev,
        dq_training: form.dq_training,
        dq_trainings: trainings,
        dq_safe_driving_awards: form.dq_safe_driving_awards,
        dq_awards: awards,
      }));

      nextStep();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <fieldset className="border p-3 mb-4">
        <legend>Qualifications</legend>

        {job && isDriver && (
          <>
            <YesNoToggle
              name="dq_conviction"
              value={form.dq_conviction}
              onChange={handleChange}
              label="Have you ever been convicted of a crime for which a pardon has not been granted?"
            />

            {form.dq_conviction === "Yes" && (
              <div className="mb-3">
                <label>
                  Date of Conviction <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  name="dq_conviction_date"
                  value={
                    form.dq_conviction_date
                      ? dayjs(form.dq_conviction_date).format("YYYY-MM-DD")
                      : ""
                  }
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            <YesNoToggle
              name="dq_fast_card"
              value={form.dq_fast_card}
              onChange={handleChange}
              label="FAST Card?"
            />

            {form.dq_fast_card === "Yes" && (
              <>
                <div className="row mb-1">
                  <div className="col-md-6">
                    <label>
                      FAST Card Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="dq_fast_card_no"
                      value={form.dq_fast_card_no || ""}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label>
                      Expiry Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      name="dq_fast_card_expiry"
                      value={
                        form.dq_fast_card_expiry
                          ? dayjs(form.dq_fast_card_expiry).format("YYYY-MM-DD")
                          : ""
                      }
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label>
                    Upload FAST Card <span className="text-danger">*</span>
                  </label>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="form-control"
                    onChange={(e) => {
                      setFastCardFileError("");
                      handleFastUpload(e.target.files);
                    }}
                  />
                  {fastCardFileError && (
                    <div className="text-danger mt-1 small">
                      {fastCardFileError}
                    </div>
                  )}

                  <ul className="list-group mt-2">
                    {fastDocs.files?.map((f) => (
                      <li
                        key={f.id}
                        className="list-group-item d-flex justify-content-between align-items-center"
                      >
                        {f.original_name}
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => handleFastDelete(f.id)}
                        >
                          Delete
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            <YesNoToggle
              name="dq_fast_card_revoked"
              value={form.dq_fast_card_revoked}
              onChange={handleChange}
              label="Have you ever had your FAST Card revoked or denied?"
            />

            {form.dq_fast_card_revoked === "Yes" && (
              <>
                <div className="mb-1">
                  <label>
                    Date of Revocation/Denial{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    name="dq_fast_card_revoked_date"
                    value={form.dq_fast_card_revoked_date || ""}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label>
                    Reason <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    name="dq_fast_card_revoked_detail"
                    value={form.dq_fast_card_revoked_detail || ""}
                    onChange={handleChange}
                    required
                  />
                </div>
              </>
            )}

            <YesNoToggle
              name="dq_accidents_last5yr"
              value={form.dq_accidents_last5yr}
              onChange={handleChange}
              label="Any accidents/incidents in the last 5 years (even if not at fault)?"
            />
            {form.dq_accidents_last5yr === "Yes" && (
              <>
                <div className="row mt-2">
                  <div className="col-md-6">
                    <label>
                      Date of accidents/incidents{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      name="dq_accidents_last5yr_date"
                      value={form.dq_accidents_last5yr_date || ""}
                      onChange={handleChange}
                      className="form-control mt-2"
                      required
                    />
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-md-6">
                    <label>
                      Please provide details{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      name="dq_accidents_last5yr_detail"
                      value={form.dq_accidents_last5yr_detail || ""}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              </>
            )}

            <YesNoToggle
              name="dq_violations_last3yr"
              value={form.dq_violations_last3yr}
              onChange={handleChange}
              label="Any moving violations / traffic convictions in the past 3 years?"
            />

            {form.dq_violations_last3yr === "Yes" && (
              <>
                <div className="row mt-2">
                  <div className="col-md-6">
                    <label>
                      Date of moving violations / traffic convictions{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      name="dq_violations_last3yr_date"
                      value={form.dq_violations_last3yr_date || ""}
                      onChange={handleChange}
                      className="form-control mt-2"
                      required
                    />
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-md-6">
                    <label>
                      Please provide details{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      name="dq_violations_last3yr_detail"
                      value={form.dq_violations_last3yr_detail || ""}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              </>
            )}
          </>
        )}

        <YesNoToggle
          name="dq_physical_condition"
          value={form.dq_physical_condition}
          onChange={handleChange}
          label="Any physical condition that may limit your ability to perform the job?"
        />
        {form.dq_physical_condition === "Yes" && (
          <div className="mb-3">
            <label>
              Please provide details <span className="text-danger">*</span>
            </label>
            <textarea
              className="form-control"
              name="dq_physical_condition_detail"
              value={form.dq_physical_condition_detail || ""}
              onChange={handleChange}
              required
            />
          </div>
        )}

        {job && isDriver && (
          <>
            <YesNoToggle
              name="dq_lift_40lb"
              value={form.dq_lift_40lb}
              onChange={handleChange}
              label="Are you able to lift above 40lb above the head?"
            />

            <YesNoToggle
              name="dq_training"
              value={form.dq_training}
              onChange={handleChange}
              label="Have you taken training courses related to this position?"
            />

            {form.dq_training === "Yes" && (
              <>
                {trainings.map((t, i) => (
                  <div key={i} className="row mb-2 align-items-end">
                    <div className="col-md-5">
                      <label>Type of Training *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={t.type}
                        onChange={(e) =>
                          handleTrainingChange(i, "type", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div className="col-md-5">
                      <label>Training Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        value={t.date}
                        onChange={(e) =>
                          handleTrainingChange(i, "date", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div className="col-md-2">
                      {trainings.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() =>
                            setTrainings(trainings.filter((_, idx) => idx !== i))
                          }
                        >
                          X
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn btn-sm btn-success mb-3"
                  onClick={addTraining}
                >
                  + Add Training
                </button>

                <div className="mb-3">
                  <label>Upload Training Cert (Optional)</label>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="form-control"
                    onChange={(e) =>
                      handleUpload(e.target.files, "training", setTrainingDocs)
                    }
                  />

                  <ul className="list-group mt-2">
                    {trainingDocs.map((doc) => (
                      <li
                        key={doc.doc_id}
                        className="list-group-item d-flex justify-content-between align-items-center"
                      >
                        {doc.doc_original}
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() =>
                            handleDelete(doc.doc_id, "training", setTrainingDocs)
                          }
                        >
                          Delete
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            <YesNoToggle
              name="dq_safe_driving_awards"
              value={form.dq_safe_driving_awards}
              onChange={handleChange}
              label="Do you have safe driving awards?"
            />

            {form.dq_safe_driving_awards === "Yes" && (
              <>
                {awards.map((a, i) => (
                  <div key={i} className="row mb-2 align-items-end">
                    <div className="col-md-5">
                      <label>Type of Award *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={a.type}
                        onChange={(e) =>
                          handleAwardChange(i, "type", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div className="col-md-5">
                      <label>Award Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        value={a.date}
                        onChange={(e) =>
                          handleAwardChange(i, "date", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div className="col-md-2">
                      {awards.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() =>
                            setAwards(awards.filter((_, idx) => idx !== i))
                          }
                        >
                          X
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn btn-sm btn-success mb-3"
                  onClick={addAward}
                >
                  + Add Award
                </button>

                <div className="mb-3">
                  <label>Upload Award Cert (Optional)</label>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="form-control"
                    onChange={(e) =>
                      handleUpload(e.target.files, "award", setAwardDocs)
                    }
                  />

                  <ul className="list-group mt-2">
                    {awardDocs.map((doc) => (
                      <li
                        key={doc.doc_id}
                        className="list-group-item d-flex justify-content-between align-items-center"
                      >
                        {doc.doc_original}
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() =>
                            handleDelete(doc.doc_id, "award", setAwardDocs)
                          }
                        >
                          Delete
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </>
        )}

        <div className="d-flex justify-content-between">
          <button className="btn btn-secondary mt-3" onClick={prevStep}>
             ← Back
          </button>
          <button type="submit" className="btn btn-primary mt-3">
            Next →
          </button>
        </div>
      </fieldset>
    </form>
  );
};

export default Step4Qualifications;
